"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { startConversation } from "@/app/actions/messaging";

export function ContactButton({
  tradespersonUserId,
  tradespersonFirstName,
  isLoggedIn,
}: {
  tradespersonUserId: string;
  tradespersonFirstName: string;
  isLoggedIn: boolean;
}) {
  const router = useRouter();
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  function handleClick() {
    if (!isLoggedIn) {
      router.push(`/login?next=/tradesperson/${tradespersonUserId}`);
      return;
    }
    setShowModal(true);
  }

  async function handleSend() {
    if (!message.trim()) {
      setError("Please write a message.");
      return;
    }
    setSending(true);
    setError("");
    const result = await startConversation(tradespersonUserId, message.trim());
    if (result.error) {
      setError(result.error);
      setSending(false);
      return;
    }
    router.push("/homeowner/messages");
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className="w-full md:w-auto px-8 py-3 bg-forest text-white font-semibold rounded-sm hover:bg-forest-light transition-colors"
      >
        Contact {tradespersonFirstName}
      </button>

      {/* Message modal */}
      {showModal && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-charcoal/50 dark:bg-black/60"
            onClick={() => setShowModal(false)}
          />
          <div className="relative bg-white rounded-md p-6 md:p-8 max-w-md w-full shadow-xl">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-text-muted hover:text-text-body"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h2 className="font-serif text-xl font-bold text-charcoal mb-2">
              Message {tradespersonFirstName}
            </h2>
            <p className="text-sm text-text-muted mb-4">
              Describe what you need help with. This is completely free.
            </p>

            {error && (
              <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-sm text-sm text-error">
                {error}
              </div>
            )}

            {/* Quick templates */}
            {!message && (
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setMessage(`Hi ${tradespersonFirstName}, I'd like a quote for a job. Here are the details:\n\nWork needed: \nLocation: \nTimeline: `)}
                  className="text-xs px-3 py-1.5 border border-border rounded-full text-text-muted hover:border-forest/40 hover:text-forest transition-colors bg-transparent cursor-pointer font-sans"
                >
                  Request a quote
                </button>
                <button
                  type="button"
                  onClick={() => setMessage(`Hi ${tradespersonFirstName}, are you available for a job in my area? I need help with `)}
                  className="text-xs px-3 py-1.5 border border-border rounded-full text-text-muted hover:border-forest/40 hover:text-forest transition-colors bg-transparent cursor-pointer font-sans"
                >
                  Check availability
                </button>
              </div>
            )}

            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder={`Hi ${tradespersonFirstName}, I need help with...`}
              className="w-full px-4 py-3 border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest resize-none"
            />
            <p className="text-xs text-text-muted text-right mb-4">
              {message.length} characters
            </p>

            <button
              onClick={handleSend}
              disabled={sending}
              className="w-full px-6 py-3 bg-forest text-white font-semibold rounded-sm hover:bg-forest-light transition-colors text-sm disabled:opacity-50"
            >
              {sending ? "Sending..." : "Send Message"}
            </button>

            <p className="text-xs text-text-muted text-center mt-3">
              Free to send. No fees, no catches.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
